import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button, CardSheetLayout, Loader, NavBar } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { confirmedTeamInfoF5 } from '@/services/buildTeam5';

export const TeamConfirmF5 = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isMobile } = useIsDesktop();
  const { userInitialData, setUserInitialData } = useAuth();
  const { pinsData } = usePinsAccount();

  const { data, isLoading } = useQuery(
    'teamConfirmedInfo',
    async () => await confirmedTeamInfoF5(),
    {
      refetchOnWindowFocus: false,
      onError: (error) => {
        const errorMsg = error as Error;
        toast.showError(errorMsg);
      },
      onSuccess: (data) => {
        if (data && data.estado === false) {
          toast.show(data.mensaje);
        }
      },
    }
  );

  const fechaActiva = useMemo(() => {
    if (userInitialData && data && data.estado) {
      return userInitialData.todasLasFechas.find(
        (fecha) => fecha.orden === data.ordenFecha
      );
    }

    return null;
  }, [data, userInitialData]);

  useEffect(() => {
    if (userInitialData) {
      setUserInitialData({
        ...userInitialData,
        tiene_equipo_f5: true,
      });
    }
  }, [setUserInitialData, userInitialData]);

  const handleClickMyTeam = () => {
    navigate(ROUTES.myTeam5, {
      replace: true,
    });
  };

  if (!data || !data.estado) {
    return null;
  }

  return (
    <>
      {<Loader isVisible={isLoading} timeout={false} />}
      <NavBar desktop>
        <CardSheetLayout
          footerComplex
          showFooter
          bgClassName="bg-confirm-team"
          bgLabel={
            isMobile
              ? 'jugador pisando una pelota de futbol'
              : 'dos jugadores jugando con una pelota de futbol'
          }
          cardClassName="px-4 lg:px-9 lg:py-8 xl:pt-11 xl:pb-[3rem]"
          innerCardClassName="lg:max-w-full"
          isPremium={pinsData.isPremium}
        >
          <div className="space-y-6">
            <h1 className="m-auto text-gdt-gray uppercase font-normal font-bebas text-3xl text-center leading-8">
              ¡Tu equipo está confirmado!
            </h1>
            <p className="text-center font-normal text-sm text-gdt-gray">
              Tus jugadores están listos para competir. Comenzarás a sumar
              puntos en la {fechaActiva?.nombre ?? '-'}{' '}
              {data?.txtInicioVeda && `(${data?.txtInicioVeda})`} del Torneo{' '}
              {data.torneo}.
            </p>
            <div className="flex gap-2">
              <Button
                className="w-full py-3"
                type="button"
                variant="primary"
                onClick={handleClickMyTeam}
              >
                Ir a Mi Equipo de Fútbol 5
              </Button>
            </div>
          </div>
        </CardSheetLayout>
      </NavBar>
    </>
  );
};
